/**
 * Shared subtitle import pipeline.
 *
 * These helpers were extracted from SubtitleImport.svelte so that the Translation
 * Import modal can run the exact same decode/clean-up steps twice (once per file)
 * without duplicating the logic. Each step is exposed separately so the caller keeps
 * control over when (and how often) the SweetAlert prompts are raised.
 */

import { projectState } from "@app/store/projectStore.svelte.js";
import { speakerState } from "@app/store/speakerStore.svelte.js";

/* CC LIB */
import decode from "@cclib/functions/decode.js";
import defaults from "@cclib/lib/defaults.js";
import formats from "@cclib/lib/formats.js";
import dropFrameCv from "@cclib/functions/special/dropFrameConvert.js";
import bufferToHex from "@cclib/functions/utility/bufferToHex.js";
import fixOverlap from "@cclib/functions/eventGroups/fixOverlap.js";
import checkForOverlaps from "@cclib/functions/eventGroups/checkForOverlaps.js";
import automaticOs from "@cclib/functions/special/automaticOffset.js";
import getAutomaticOffsetAmount from "@cclib/functions/special/getAutomaticOffsetAmount.js";
import stripTags from "@cclib/functions/quill/stripTags.js";
import encodeHtml from "@cclib/functions/eventGroups/encodeHtml.js";
import tcLib from "@cclib/lib/timecode.js";
import incodeTime from "@cclib/lib/incodeTime.js";

import * as mammoth from "mammoth/mammoth.browser";
import * as ExcelJS from "exceljs";

/**
 * The project incode formatted as a clock timecode label.
 * @returns {string}
 */
export function getProjectClockIncode() {
    return incodeTime.mediaSecondsToIncodeLabel(projectState.incode, projectState.frameRate, projectState.dropFrame);
}

/**
 * A Project Incode set by the user takes priority over the automatic (whole hour) offset
 * detected from the file. The automatic offset only applies when the project incode is 0.
 * @returns {boolean}
 */
export function shouldApplyAutomaticOffset() {
    return !(projectState.incode > 0);
}

/**
 * Drop frame is stored as a string ("true"/"false") by the select inputs.
 * @param {boolean|string} dropFrame
 * @returns {boolean}
 */
export function isDropFrameEnabled(dropFrame) {
    return dropFrame === true || dropFrame === "true";
}

/**
 * Formats using clock time do not need the incode remap on decode.
 * @param {string} srcProfile
 * @returns {"yes"|"no"}
 */
export function getUseClockTimeDefault(srcProfile) {
    return srcProfile && formats.clockTimeFormats.indexOf(srcProfile) > -1 ? "no" : "yes";
}

/**
 * True when the profile expects the file to be read as a binary buffer.
 * @param {string} srcProfile
 * @returns {boolean}
 */
export function isBinaryProfile(srcProfile) {
    return !!srcProfile && formats.binaryFormats.includes(formats.profileMapping[srcProfile].name);
}

/**
 * A per-consumer copy of a profile's decode options. formats.profileMapping is a
 * module level singleton, so binding UI inputs straight into it leaks the selected
 * values between files and between modal openings.
 * @param {string} srcProfile
 * @returns {Array}
 */
export function cloneDecodeOptions(srcProfile) {
    if (!srcProfile || !formats.profileMapping[srcProfile] || !formats.profileMapping[srcProfile].options) {
        return [];
    }

    return structuredClone(formats.profileMapping[srcProfile].options.decode ?? []);
}

/**
 * Reads a subtitle file into the string the decoder expects, handling the
 * docx / xlsx / binary / plain text branches.
 * @param {File} file
 * @param {{srcExt: string, srcProfile: string, fileEncoding: string}} options
 * @returns {Promise<string>}
 */
export function readSubtitleFileContents(file, { srcExt, srcProfile, fileEncoding }) {
    return new Promise((resolve, reject) => {
        if (!file) {
            resolve("");
            return;
        }

        const extension = (srcExt || "").toLowerCase();
        const reader = new FileReader();

        reader.onerror = function () {
            reject(reader.error || new Error("Unable to read file"));
        };

        reader.onload = async function (e) {
            try {
                if (extension === "docx") {
                    let result =
                        srcProfile === "MS2 Transcript"
                            ? await mammoth.convertToHtml({ arrayBuffer: e.target.result })
                            : await mammoth.extractRawText({ arrayBuffer: e.target.result });

                    resolve(result.value);
                } else if (extension === "xlsx") {
                    const workbook = new ExcelJS.Workbook();
                    await workbook.xlsx.load(e.target.result);
                    resolve(JSON.stringify(workbook.model, null, 4));
                } else if (isBinaryProfile(srcProfile)) {
                    resolve(bufferToHex(e.target.result));
                } else {
                    resolve(e.target.result);
                }
            } catch (err) {
                reject(err);
            }
        };

        if (isBinaryProfile(srcProfile) || extension === "docx" || extension === "xlsx") {
            reader.readAsArrayBuffer(file);
        } else {
            reader.readAsText(file, fileEncoding);
        }
    });
}

/**
 * Validates a source incode against the source frame rate.
 * @param {string} srcIncode
 * @param {number|string} srcFrameRate
 * @param {boolean|string} srcDropFrame
 * @returns {string}
 */
export function validateIncode(srcIncode, srcFrameRate, srcDropFrame) {
    return tcLib.tcValidate(srcIncode, parseFloat(srcFrameRate), isDropFrameEnabled(srcDropFrame));
}

/**
 * Builds the cc-lib decode options object, including the non-clock-time incode remap.
 * @param {object} settings
 * @returns {object}
 */
export function buildDecodeOptions({
    srcProfile,
    srcFrameRate,
    srcDropFrame,
    srcIncode,
    srcUseClockTime,
    formatOptions,
    window,
}) {
    const dropFrame = isDropFrameEnabled(srcDropFrame);
    let incode = validateIncode(srcIncode, srcFrameRate, dropFrame);

    if (srcUseClockTime === "no") {
        incode = incodeTime.remapIncodeForNonClockPipeline(incode, parseFloat(srcFrameRate), dropFrame);
    }

    return new defaults.options({
        profile: formats.profileMapping[srcProfile].name,
        formatOptions: formatOptions ?? formats.profileMapping[srcProfile].options.decode,
        frameRate: srcFrameRate,
        dropFrame: dropFrame,
        target_profile: "closedCaptionProject",
        source_profile: formats.profileMapping[srcProfile].name,
        incode: incode,
        window: window,
    });
}

/**
 * Decodes the file contents into an event group.
 * @param {string} fileContents
 * @param {object} options
 * @returns {Promise<object>}
 */
export async function decodeSubtitleSource(fileContents, options) {
    let decodedEvGroup = await decode(fileContents, options);
    return encodeHtml(decodedEvGroup);
}

/**
 * The offset (in seconds) between the decoded group and the project incode.
 * Always 0 when the user has set a Project Incode (see shouldApplyAutomaticOffset).
 * @param {object} eventGroup
 * @returns {number}
 */
export function detectAutomaticOffset(eventGroup) {
    if (!shouldApplyAutomaticOffset() || !eventGroup.events.length) {
        return 0;
    }

    return getAutomaticOffsetAmount(
        eventGroup,
        projectState.frameRate,
        projectState.dropFrame,
        tcLib.secToTc(projectState.incode, projectState.frameRate, projectState.dropFrame)
    );
}

/**
 * @param {object} eventGroup
 * @returns {object}
 */
export function applyAutomaticOffset(eventGroup) {
    return automaticOs(eventGroup, projectState.frameRate, projectState.dropFrame);
}

/**
 * Blank event stripping and overlap fixing only apply to Pop-On/Paint-On groups.
 * @param {object} eventGroup
 * @returns {boolean}
 */
export function isPopOnOrPaintOnGroup(eventGroup) {
    return eventGroup.events.every((ev) => ev.style === "Pop-On" || ev.style === "Paint-On");
}

/**
 * Removes events with no text, notes or reply.
 * @param {object} eventGroup
 * @returns {object}
 */
export function stripBlankEvents(eventGroup) {
    if (!isPopOnOrPaintOnGroup(eventGroup)) {
        return eventGroup;
    }

    eventGroup.events = eventGroup.events.filter((event) => {
        return stripTags(event.text) || event.notes || event.reply;
    });

    return eventGroup;
}

/**
 * @param {object} eventGroup
 * @returns {boolean}
 */
export function hasOverlaps(eventGroup) {
    return isPopOnOrPaintOnGroup(eventGroup) && checkForOverlaps(eventGroup);
}

/**
 * @param {object} eventGroup
 * @returns {object}
 */
export function applyOverlapFix(eventGroup) {
    return fixOverlap(eventGroup);
}

/**
 * Adds any speaker found on the decoded events to the project speaker list.
 * @param {object} eventGroup
 */
export function importSpeakersFromGroup(eventGroup) {
    eventGroup.events.forEach((event) => {
        event.speakers.forEach((speaker) => {
            //Check if the speaker already exists in speakerState, otherwise add it.
            if (!speakerState.find((s) => s.name === speaker.name)) {
                speakerState.push(speaker);
            }
        });
    });
}

/**
 * @param {object} eventGroup
 * @returns {object}
 */
export function applyDropFrameConvert(eventGroup) {
    return dropFrameCv(eventGroup, {
        frameRate: projectState.frameRate,
        dropFrame: projectState.dropFrame,
    });
}
